import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import { BiExport } from "react-icons/bi";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import DashboardBranchFilter, { useDashboardBranch } from "../Includes/DashboardBranchFilter";
import { ATTENDANCE_BRANCH_PICKER_ROLES } from "../../utils/branchAccess";
import useSortableData from "../../hooks/useSortableData";
import "./EmployeeAttendance.css";

const CARDS = [
  { key: "", label: "All", note: "Every status in this range", tone: "tone-slate" },
  { key: "not_arrived", label: "Not arrived", note: "Shift started, no check-in", tone: "tone-red" },
  { key: "late", label: "Late", note: "After the 15 minute grace", tone: "tone-amber" },
  { key: "on_time", label: "On time", note: "Checked in on time", tone: "tone-green" },
  { key: "leave", label: "On leave", note: "Approved or recorded leave", tone: "tone-blue" },
  { key: "absent", label: "Marked absent", note: "Stored as absent", tone: "tone-red" },
  { key: "due_later", label: "Due later", note: "Shift has not started", tone: "tone-slate" },
  { key: "off", label: "Off today", note: "No duty hours", tone: "tone-slate" },
];

function StaffPhoto({ src, name }) {
  const [failed, setFailed] = useState(false);
  const initials = String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  if (!src || failed) {
    return <span className="ea-photo ea-photo-empty" aria-hidden="true">{initials || "?"}</span>;
  }

  return <img className="ea-photo" src={src} alt="" onError={() => setFailed(true)} />;
}

const EMPTY_COUNTS = {
  scheduled: 0,
  on_time: 0,
  late: 0,
  not_arrived: 0,
  absent: 0,
  leave: 0,
  due_later: 0,
  off: 0,
};

const text = (value) => {
  const cleaned = String(value ?? "").trim();
  return cleaned && cleaned !== "null" ? cleaned : "";
};

export default function Employeeattendance() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const { branch, setBranch, apiBranch } = useDashboardBranch(role, branch_id, {
    extraRoles: ATTENDANCE_BRANCH_PICKER_ROLES,
  });
  const [detailFrom, setDetailFrom] = useState(moment().format("YYYY-MM-DD"));
  const [detailTo, setDetailTo] = useState(moment().format("YYYY-MM-DD"));
  const [departmentId, setDepartmentId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const [query, setQuery] = useState("");
  const [card, setCard] = useState("");
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [people, setPeople] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [weekday, setWeekday] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState("day");
  const [summaryFrom, setSummaryFrom] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [summaryTo, setSummaryTo] = useState(moment().format("YYYY-MM-DD"));
  const [summary, setSummary] = useState({
    totals: { present: 0, on_time: 0, late: 0, absent: 0, leave: 0 },
    employees: [],
  });

  useEffect(() => {
    let cancelled = false;
    const loadLists = async () => {
      try {
        const [departmentResponse, designationResponse] = await Promise.all([
          UserServices.departmentName(),
          UserServices.designationName(),
        ]);
        if (cancelled) return;
        setDepartments(departmentResponse?.data?.data || []);
        setDesignations(designationResponse?.data?.data || []);
      } catch (error) {
        if (!cancelled) {
          setDepartments([]);
          setDesignations([]);
        }
      }
    };
    loadLists();
    return () => {
      cancelled = true;
    };
  }, []);

  const detailRangeInvalid = !detailFrom || !detailTo || detailFrom > detailTo;
  const detailRangeTooLong =
    !detailRangeInvalid && moment(detailTo).diff(moment(detailFrom), "days") > 61;

  useEffect(() => {
    if (view !== "day" || detailRangeInvalid || detailRangeTooLong) return undefined;
    let cancelled = false;
    const loadBoard = async () => {
      setLoading(true);
      try {
        const response = await UserServices.getEmployeeAttendanceDayBoard({
          branch_id: apiBranch,
          start_date: detailFrom,
          end_date: detailTo,
          department_id: departmentId,
          designation_id: designationId,
        });
        if (cancelled) return;
        const data = response?.data?.data || {};
        setPeople(Array.isArray(data.people) ? data.people : []);
        setCounts({ ...EMPTY_COUNTS, ...(data.counts || {}) });
        setWeekday(data.weekday || "");
      } catch (error) {
        if (!cancelled) {
          setPeople([]);
          setCounts(EMPTY_COUNTS);
          setWeekday("");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadBoard();
    return () => {
      cancelled = true;
    };
  }, [view, apiBranch, detailFrom, detailTo, detailRangeInvalid, detailRangeTooLong, departmentId, designationId]);

  useEffect(() => {
    if (view !== "summary") return undefined;
    if (!summaryFrom || !summaryTo || summaryFrom > summaryTo) return undefined;
    let cancelled = false;
    const loadSummary = async () => {
    setLoading(true);
      try {
        const response = await UserServices.getEmployeeAttendanceReportSummary({
          branch_id: apiBranch,
          start_date: summaryFrom,
          end_date: summaryTo,
          department_id: departmentId,
          designation_id: designationId,
        });
        if (cancelled) return;
        const data = response?.data?.data || {};
        setSummary({
          totals: { present: 0, on_time: 0, late: 0, absent: 0, leave: 0, ...(data.totals || {}) },
          employees: Array.isArray(data.employees) ? data.employees : [],
        });
      } catch (error) {
        if (!cancelled) {
          setSummary({
            totals: { present: 0, on_time: 0, late: 0, absent: 0, leave: 0 },
            employees: [],
          });
        Swal.fire({
          icon: "error",
            title: "Summary failed",
            text: "Could not load the attendance summary for these dates.",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [view, apiBranch, summaryFrom, summaryTo, departmentId, designationId]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return people.filter((person) => {
      if (card && person.status !== card) return false;
      if (!needle) return true;
      const haystack = [person.name, person.uid, person.department, person.designation, person.branch]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [people, card, query]);

  const summaryRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (summary.employees || []).filter((person) => {
      if (!needle) return true;
      return [person.employee_name, person.uid, person.department, person.designation, person.branch]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [summary.employees, query]);

  const { items, requestSort, sortConfig } = useSortableData(visible);
  const {
    items: summaryItems,
    requestSort: requestSummarySort,
    sortConfig: summarySort,
  } = useSortableData(summaryRows);
  const activeCard = CARDS.find((item) => item.key === card) || CARDS[0];
  const fromLabel = moment(detailFrom, "YYYY-MM-DD", true);
  const toLabel = moment(detailTo, "YYYY-MM-DD", true);
  const dateLabel =
    fromLabel.isValid() && toLabel.isValid()
      ? detailFrom === detailTo
        ? `${weekday || fromLabel.format("dddd")} · ${fromLabel.format("D MMM YYYY")}`
        : `${fromLabel.format("D MMM YYYY")} to ${toLabel.format("D MMM YYYY")}`
      : "Select dates";

  const sortClass = (key) => (sortConfig?.key === key ? sortConfig.direction : undefined);

  const downloadAttendance = async (format) => {
    setExporting(true);
    try {
      const response = await UserServices.exportEmployeeAttendance({
        branch_id: apiBranch,
        start_date: view === "summary" ? summaryFrom : detailFrom,
        end_date: view === "summary" ? summaryTo : detailTo,
        department_id: departmentId,
        designation_id: designationId,
        format,
      });
      const extension = format === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([response.data], {
        type:
          format === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const exportFrom = view === "summary" ? summaryFrom : detailFrom;
      const exportTo = view === "summary" ? summaryTo : detailTo;
      link.download = `Staff Attendance ${exportFrom} to ${exportTo}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Export failed",
        text: "Could not download the attendance file for this date.",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design ea-page">
          <MenuButton />
          <header className="ea-head">
            <div>
              <p className="ea-kicker">Human resources</p>
              <h1>Employee Attendance</h1>
              <p>{view === "summary" ? `${summaryFrom} to ${summaryTo}` : dateLabel}</p>
            </div>
            <div className="ea-head-actions">
              <div className="ea-view" role="tablist">
                <button type="button" className={view === "day" ? "active" : ""} onClick={() => setView("day")}>
                  Detail
                </button>
                <button type="button" className={view === "summary" ? "active" : ""} onClick={() => setView("summary")}>
                  Summary
                </button>
              </div>
              <button type="button" className="ea-btn" disabled={exporting} onClick={() => downloadAttendance("excel")}>
                <BiExport /> Excel
              </button>
              <button type="button" className="ea-btn" disabled={exporting} onClick={() => downloadAttendance("pdf")}>
                <BiExport /> PDF
              </button>
            </div>
          </header>

          {view === "day" ? (
          <div className="ea-cards">
            {CARDS.map((item) => (
              <button
                key={item.key || "all"}
                type="button"
                className={`ea-card ${item.tone} ${card === item.key ? "selected" : ""}`}
                onClick={() => setCard(item.key)}
              >
                <strong>{item.key ? counts[item.key] || 0 : people.length}</strong>
                <span>{item.label}</span>
                <small>{item.note}</small>
              </button>
            ))}
                      </div>
          ) : (
          <div className="ea-cards ea-summary-cards">
            {[
              ["present", "Present"],
              ["on_time", "On time"],
              ["late", "Late"],
              ["absent", "Absent"],
              ["leave", "Leave"],
            ].map(([key, label]) => (
              <div key={key} className="ea-card">
                <strong>{summary.totals?.[key] || 0}</strong>
                <span>{label}</span>
                <small>For the selected dates</small>
                        </div>
            ))}
                        </div>
          )}

          <section className="ea-panel">
            <div className="ea-filters">
              <div className="ea-field">
                <label>Branch</label>
                <DashboardBranchFilter
                  role={role}
                  loginBranchId={branch_id}
                  value={branch}
                  onChange={setBranch}
                  extraRoles={ATTENDANCE_BRANCH_PICKER_ROLES}
                />
                      </div>
              {view === "summary" ? (
                <>
                  <div className="ea-field">
                    <label>From</label>
                    <input type="date" value={summaryFrom} onChange={(event) => setSummaryFrom(event.target.value)} />
                  </div>
                  <div className="ea-field">
                    <label>To</label>
                    <input type="date" value={summaryTo} onChange={(event) => setSummaryTo(event.target.value)} />
                  </div>
                </>
              ) : (
                <>
                  <div className="ea-field">
                    <label>From</label>
                    <input type="date" value={detailFrom} onChange={(event) => setDetailFrom(event.target.value)} />
                  </div>
                  <div className="ea-field">
                    <label>To</label>
                    <input type="date" value={detailTo} onChange={(event) => setDetailTo(event.target.value)} />
                  </div>
                </>
              )}
              <div className="ea-field">
                <label>Department</label>
                <select value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}>
                  <option value="">All departments</option>
                  {departments.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                      </div>
              <div className="ea-field">
                <label>Designation</label>
                <select value={designationId} onChange={(event) => setDesignationId(event.target.value)}>
                  <option value="">All designations</option>
                  {designations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                                  </option>
                  ))}
                            </select>
                        </div>
              <div className="ea-field">
                <label>Find staff</label>
                <input
                  type="search"
                  placeholder="Name, ID, or department"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
            </div>

            <div className="ea-table-wrap">
              {view === "summary" ? (
              <table className="ea-table">
                <thead>
                  <tr>
                    <th onClick={() => requestSummarySort("uid")} className={summarySort?.key === "uid" ? summarySort.direction : undefined}>Employee ID</th>
                    <th onClick={() => requestSummarySort("employee_name")} className={summarySort?.key === "employee_name" ? summarySort.direction : undefined}>Employee</th>
                    <th onClick={() => requestSummarySort("department")} className={summarySort?.key === "department" ? summarySort.direction : undefined}>Department</th>
                    <th onClick={() => requestSummarySort("designation")} className={summarySort?.key === "designation" ? summarySort.direction : undefined}>Designation</th>
                    <th onClick={() => requestSummarySort("branch")} className={summarySort?.key === "branch" ? summarySort.direction : undefined}>Branch</th>
                    <th onClick={() => requestSummarySort("present_count")} className={summarySort?.key === "present_count" ? summarySort.direction : undefined}>Present</th>
                    <th onClick={() => requestSummarySort("on_time_count")} className={summarySort?.key === "on_time_count" ? summarySort.direction : undefined}>On time</th>
                    <th onClick={() => requestSummarySort("late_count")} className={summarySort?.key === "late_count" ? summarySort.direction : undefined}>Late</th>
                    <th onClick={() => requestSummarySort("absent_count")} className={summarySort?.key === "absent_count" ? summarySort.direction : undefined}>Absent</th>
                    <th onClick={() => requestSummarySort("leave_count")} className={summarySort?.key === "leave_count" ? summarySort.direction : undefined}>Leave</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="ea-empty">Loading summary…</td></tr>
                  ) : summaryItems.length ? (
                    summaryItems.map((person) => (
                      <tr key={person.id}>
                        <td>{text(person.uid) || "—"}</td>
                        <td>
                          <div className="ea-person">
                            <StaffPhoto src={person.image} name={person.employee_name} />
                            <Link className="ea-name" to={`/staff-profile/${person.id}`}>
                              {text(person.employee_name) || "Unnamed"}
                            </Link>
                          </div>
                        </td>
                        <td>{text(person.department) || "—"}</td>
                        <td>{text(person.designation) || "—"}</td>
                        <td>{text(person.branch) || "—"}</td>
                        <td>{person.present_count || 0}</td>
                        <td>{person.on_time_count || 0}</td>
                        <td>{person.late_count || 0}</td>
                        <td>{person.absent_count || 0}</td>
                        <td>{person.leave_count || 0}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="10" className="ea-empty">No attendance in this date range.</td></tr>
                  )}
                </tbody>
              </table>
              ) : (
              <table className="ea-table">
                <thead>
                  <tr>
                    <th onClick={() => requestSort("date")} className={sortClass("date")}>Date</th>
                    <th onClick={() => requestSort("name")} className={sortClass("name")}>Employee</th>
                    <th onClick={() => requestSort("department")} className={sortClass("department")}>Department</th>
                    <th onClick={() => requestSort("designation")} className={sortClass("designation")}>Designation</th>
                    <th onClick={() => requestSort("branch")} className={sortClass("branch")}>Branch</th>
                    <th>Duty</th>
                    <th>In</th>
                    <th>Out</th>
                    <th onClick={() => requestSort("status_label")} className={sortClass("status_label")}>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="ea-empty">Loading attendance…</td>
                    </tr>
                  ) : detailRangeInvalid ? (
                    <tr>
                      <td colSpan="10" className="ea-empty">The From date has to be on or before the To date.</td>
                    </tr>
                  ) : detailRangeTooLong ? (
                    <tr>
                      <td colSpan="10" className="ea-empty">Choose 62 days or less.</td>
                    </tr>
                  ) : items.length ? (
                    items.map((person) => (
                      <tr key={person.row_key || `${person.id}-${person.date}`}>
                        <td>{person.date ? moment(person.date).format("DD MMM YYYY") : "—"}</td>
                        <td>
                          <div className="ea-person">
                            <StaffPhoto src={person.image} name={person.name} />
                            <div>
                              <Link className="ea-name" to={`/staff-profile/${person.id}`}>
                                {text(person.name) || "Unnamed"}
                              </Link>
                              <div className="ea-sub">{text(person.uid) || "No employee ID"}</div>
                            </div>
                          </div>
                        </td>
                        <td>{text(person.department) || "—"}</td>
                        <td>{text(person.designation) || "—"}</td>
                        <td>{text(person.branch) || "—"}</td>
                        <td>
                          {text(person.duty_start)
                            ? `${person.duty_start}${text(person.duty_end) ? ` – ${person.duty_end}` : ""}`
                            : "—"}
                        </td>
                        <td>{text(person.checkin) || "—"}</td>
                        <td>{text(person.checkout) || "—"}</td>
                        <td>
                          <span className={`ea-badge ${person.status}`}>{person.status_label}</span>
                        </td>
                        <td>{text(person.detail) || "—"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="ea-empty">
                        {card
                          ? `No staff in ${activeCard.label.toLowerCase()} for these dates.`
                          : "No staff for these dates."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
